import React, { useState } from 'react';
import { useChat } from '../../contexts/ChatContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faPlus, faTimes, faUser } from '@fortawesome/free-solid-svg-icons';
import api from '../../services/api';
import '../../assets/styles/Chat.css';

const SessionItem = ({ session, isActive, onClick }) => {
  // 格式化最后消息时间
  const formatTime = (timeString) => {
    if (!timeString) return '';
    
    const date = new Date(timeString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      // 今天，显示时:分
      return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      // 昨天
      return '昨天';
    } else if (diffDays < 7) {
      // 一周内，显示星期几
      const weekdays = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
      return weekdays[date.getDay()];
    } else {
      // 超过一周，显示年-月-日
      return date.toLocaleDateString('zh-CN');
    }
  };

  return (
    <div 
      className={`session-item ${isActive ? 'active' : ''}`} 
      onClick={() => onClick(session.sessionId)}
    >
      <div className="session-avatar">
        <img src={session.displayAvatar} alt={session.displayName} />
      </div>
      <div className="session-info">
        <div className="session-header">
          <span className="session-name">{session.displayName}</span>
          <span className="session-time">{formatTime(session.lastMessageTime)}</span>
        </div>
        <div className="session-footer">
          <span className="session-last-message">{session.lastMessageContent || '暂无消息'}</span>
          {session.unreadCount > 0 && (
            <span className="session-badge">{session.unreadCount > 99 ? '99+' : session.unreadCount}</span>
          )}
        </div>
      </div>
    </div>
  );
};

const SessionList = () => {
  const { sessions, currentSession, loadSessions, switchSession, findSingleSession } = useChat();
  const [searchKeyword, setSearchKeyword] = useState('');
  const [sessionType, setSessionType] = useState('');
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [userSearchKeyword, setUserSearchKeyword] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const handleSearch = () => {
    loadSessions(searchKeyword, sessionType);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // 搜索用户
  const searchUsers = async (keyword) => {
    if (!keyword.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await api.fetchWithToken(`/user/searchUsers?keyword=${encodeURIComponent(keyword)}`, {
        method: 'GET'
      });

      if (response.success) {
        setSearchResults(response.data || []);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error('搜索用户失败:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // 处理用户搜索输入
  const handleUserSearchChange = (e) => {
    const value = e.target.value;
    setUserSearchKeyword(value);
    
    // 防抖搜索
    clearTimeout(window.userSearchTimeout);
    window.userSearchTimeout = setTimeout(() => {
      searchUsers(value);
    }, 300);
  };

  // 发起新聊天
  const handleStartChat = async (userId) => {
    try {
      const newSession = await findSingleSession(userId);
      if (newSession) {
        setShowNewChatModal(false);
        setUserSearchKeyword('');
        setSearchResults([]);
        // 切换到新会话
        switchSession(newSession.sessionId);
      }
    } catch (error) {
      console.error('发起聊天失败:', error);
    }
  };

  // 打开新聊天弹窗
  const handleAddButtonClick = () => {
    setShowNewChatModal(true);
  };

  // 关闭新聊天弹窗
  const handleCloseNewChatModal = () => {
    setShowNewChatModal(false);
    setUserSearchKeyword('');
    setSearchResults([]);
  };

  return (
    <>
      <div className="session-list">
        <div className="session-list-header">
          <div className="search-container">
            <input
              type="text"
              placeholder="搜索"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <button className="search-button" onClick={handleSearch}>
              <FontAwesomeIcon icon={faSearch} />
            </button>
          </div>
          <button className="add-button" onClick={handleAddButtonClick}>
            <FontAwesomeIcon icon={faPlus} />
          </button>
        </div>
        <div className="session-list-content">
          {sessions.length === 0 ? (
            <div className="no-sessions">暂无会话</div>
          ) : (
            sessions.map(session => (
              <SessionItem
                key={session.sessionId}
                session={session}
                isActive={currentSession && currentSession.sessionId === session.sessionId}
                onClick={switchSession}
              />
            ))
          )}
        </div>
      </div>

      {/* 新聊天弹窗 */}
      {showNewChatModal && (
        <div className="new-chat-modal-overlay" onClick={handleCloseNewChatModal}>
          <div className="new-chat-modal" onClick={(e) => e.stopPropagation()}>
            <div className="new-chat-modal-header">
              <h3>发起新聊天</h3>
              <button className="new-chat-modal-close" onClick={handleCloseNewChatModal}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="new-chat-modal-body">
              <div className="user-search-container">
                <input
                  type="text"
                  placeholder="搜索用户..."
                  value={userSearchKeyword}
                  onChange={handleUserSearchChange}
                  className="user-search-input"
                />
                <FontAwesomeIcon icon={faSearch} className="user-search-icon" />
              </div>
              
              <div className="user-search-results">
                {isSearching ? (
                  <div className="search-loading">搜索中...</div>
                ) : searchResults.length > 0 ? (
                  searchResults.map(user => (
                    <div 
                      key={user.id} 
                      className="user-search-item"
                      onClick={() => handleStartChat(user.id)}
                    >
                      <div className="user-search-avatar">
                        <img 
                          src={user.avatarUrl || 'https://via.placeholder.com/40'} 
                          alt={user.nickname || user.userAccount} 
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://via.placeholder.com/40';
                          }}
                        />
                      </div>
                      <div className="user-search-info">
                        <div className="user-search-name">
                          {user.nickname || user.userAccount || '用户'}
                        </div>
                        <div className="user-search-account">
                          @{user.userAccount || user.id}
                        </div>
                      </div>
                      <div className="user-search-action">
                        <FontAwesomeIcon icon={faUser} />
                      </div>
                    </div>
                  ))
                ) : userSearchKeyword.trim() ? (
                  <div className="no-search-results">未找到相关用户</div>
                ) : (
                  <div className="search-placeholder">输入用户名或账号进行搜索</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SessionList; 